import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { submitIdea } from '../../api/idea';
import { downloadTemplate } from '../../api/template';
import { toast } from 'sonner';

export function SubmitIdea() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();

  const hackathonId = searchParams.get('hackathonId') ?? '';
  const hackathonTitle = searchParams.get('hackathonTitle') ?? 'Hackathon';
  const problemTitle = searchParams.get('problemTitle') ?? 'Idea Statement';
  const problemDescription = searchParams.get('problemDescription') || searchParams.get('problemTitle') || '';

  const [title, setTitle] = useState('');
  const [teamName, setTeamName] = useState('');
  const [solution, setSolution] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) { setError('You must be logged in.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      // teamId is resolved server-side via the auth token + hackathonId registration lookup
      // We pass a placeholder; the backend resolves the team from the registration
      await submitIdea(token, { hackathonId, title, problem: problemDescription, solution, attachments });
      toast.success('Idea submitted successfully!');
      navigate('/user-dashboard/idea-list');
    } catch (err: any) {
      setError(err.message ?? 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      await downloadTemplate(hackathonTitle, problemDescription);
      toast.success('Template downloaded successfully!');
    } catch (err: any) {
      toast.error('Failed to download template');
      console.error(err);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl text-gray-900 mb-2">Submit Your Idea</h1>
            <p className="text-gray-500">Share your innovative solution with mentors and jury</p>
          </div>

          <div className="bg-white rounded-xl p-6 mb-6 flex justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Hackathon</p>
              <p className="text-gray-900 font-medium">{hackathonTitle}</p>
            </div>
              <div className="text-right">
              <p className="text-sm text-gray-500 mb-1">Idea Statement</p>
              <p className="text-gray-900 font-medium">{problemTitle}</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 space-y-6">
            <div>
              <label className="block text-sm mb-2 text-gray-900">
                Registered Team Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={teamName}
                onChange={e => setTeamName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-2 text-gray-900">
                Idea Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-2 text-gray-900">
                Idea Statement <span className="text-red-500">*</span>
              </label>
              <textarea
                value={problemDescription}
                rows={4}
                readOnly
                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 outline-none resize-none cursor-default"
              />
            </div>

            <div>
              <label className="block text-sm mb-2 text-gray-900">
                Proposed Solution <span className="text-red-500">*</span>
              </label>
              <textarea
                value={solution}
                onChange={e => setSolution(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                required
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">PowerPoint Template</h3>
              <p className="text-sm text-blue-800 mb-4">
                Download our template to format your idea presentation. Fill it with your details and upload the completed file.
              </p>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {downloadingTemplate ? 'Downloading...' : '⬇️ Download Template (PPTX)'}
              </button>
            </div>

            <div>
              <label className="block text-sm mb-2 text-gray-900">Upload Presentation & Attachments</label>
              <input
                type="file"
                multiple
                onChange={e => setAttachments(Array.from(e.target.files ?? []))}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {attachments.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {attachments.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg">
                      <span>{f.name}</span>
                      <button type="button" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 ml-2">✕</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-8 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Idea'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
